// Mode toggle — sits above the panels and switches between
// Renovation and New Build. When the deal has populated values, asks
// for confirmation since several panel labels and computed fields get
// reinterpreted on switch.

import {DealMode} from '../defaults';

export interface IModeToggleHost {
	getMode(): DealMode;
	dealHasData(): boolean;
	setMode(mode: DealMode): void;
}

const REINTERPRET_MSG =
	'Switching mode will reinterpret some inputs (renovation costs, value-add fields, panel labels). Continue?';

export class ModeToggle {
	private rootEl: HTMLElement | null;
	private renoBtn: HTMLButtonElement | null = null;
	private buildBtn: HTMLButtonElement | null = null;

	constructor(private host: IModeToggleHost) {
		this.rootEl = document.getElementById('id_lb-deal-editor-mode-toggle');
		if (!this.rootEl) {
			return;
		}
		this.scaffold();
		this.render();
	}

	render(): void {
		if (!this.renoBtn || !this.buildBtn) {
			return;
		}
		const mode = this.host.getMode();
		this.renoBtn.setAttribute('aria-pressed', String(mode === 'reno'));
		this.buildBtn.setAttribute('aria-pressed', String(mode === 'build'));
		this.renoBtn.classList.toggle('lb-deal-mode-btn--active', mode === 'reno');
		this.buildBtn.classList.toggle('lb-deal-mode-btn--active', mode === 'build');
	}

	private handle(next: DealMode): void {
		if (next === this.host.getMode()) {
			return;
		}
		if (this.host.dealHasData() && !window.confirm(REINTERPRET_MSG)) {
			return;
		}
		this.host.setMode(next);
		this.render();
	}

	private iconButton(icon: string, label: string, onClick: () => void): HTMLButtonElement {
		const btn = document.createElement('button');
		btn.type = 'button';
		btn.className = 'lb-deal-mode-btn';
		const iconEl = document.createElement('span');
		iconEl.className = 'material-icons';
		iconEl.setAttribute('aria-hidden', 'true');
		iconEl.textContent = icon;
		btn.appendChild(iconEl);
		const labelEl = document.createElement('span');
		labelEl.textContent = label;
		btn.appendChild(labelEl);
		btn.addEventListener('click', onClick);
		return btn;
	}

	private scaffold(): void {
		if (!this.rootEl) {
			return;
		}
		while (this.rootEl.firstChild) {
			this.rootEl.removeChild(this.rootEl.firstChild);
		}
		const group = document.createElement('div');
		group.className = 'lb-deal-mode-toggle';

		this.renoBtn = this.iconButton('build', 'Renovation', () => this.handle('reno'));
		group.appendChild(this.renoBtn);
		this.buildBtn = this.iconButton('construction', 'New Build', () => this.handle('build'));
		group.appendChild(this.buildBtn);

		this.rootEl.appendChild(group);
	}
}
